import styled from '@emotion/styled';
import React from 'react';

import { textIsDimmedDeprecated } from '../../console-messages';
import type { LensSpanProps } from '../../types';
import {
  getColorValue,
  getFontSetting,
  getFontWeight,
  getTextSize,
  u,
} from '../../utilities';
import { type TextSize, textSizes, unit } from '../../variables';

export const dimAmount = 0.6;

// We have to export this so the docs can read it
export enum FontFamily {
  'sans-serif' = 'inherit', // We set this to inherit as the default as Atlassian Sans wil fall into place
  'mono' = 'Atlassian Mono',
}

type VariantsProps = {
  [key: string]: {
    size: TextProps['size'];
    fontWeight: TextProps['fontWeight'];
  };
};

export const variants: VariantsProps = {
  body: {
    size: 'body-md',
    fontWeight: 'regular',
  },
  title: {
    size: 'body-lg',
    fontWeight: 'bold',
  },
  mainTitle: {
    size: 'heading-md',
    fontWeight: 'bold',
  },
};

const getTextPxSize = size => textSizes[size].fontSize * unit;

const getTextUnitSize = size => u(textSizes[size].fontSize);

export const lineHeightToPx = size =>
  textSizes[size].fontSize * textSizes[size].lineHeight * unit;

const clampBuilder = (minSize, maxSize, minWindow, maxWindow) => {
  const slope = (maxSize - minSize) / (maxWindow - minWindow);
  const yAxisIntersection = -minWindow * slope + minSize;

  return `${yAxisIntersection}px + ${slope * 100}vw`;
};

const TextWrapper = styled.span<TextWrapperProps>`
  display: ${props => (props.isInline ? 'inline' : 'block')};
  ${props => !props.sizeMinMax && getTextSize(props.size)};
  ${props => getFontWeight(props.fontWeight)};
  ${props => props.color && `color: ${getColorValue(props.color)}`};
  ${props =>
    props.fontSetting &&
    `font-feature-settings: ${getFontSetting(props.fontSetting)}`};
  ${props => props.isDimmed && `opacity: ${dimAmount}`};
  ${props => props.alignment && `text-align: ${props.alignment}`};
  ${props => props.overflowWrap && `overflow-wrap: ${props.overflowWrap};`};
  ${props =>
    props.hasEllipsis &&
    !props.ellipsisLines &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `};
  ${props =>
    props.hasEllipsis &&
    props.ellipsisLines &&
    `
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${props.ellipsisLines};
  `};
  ${props =>
    !props.hasEllipsis &&
    props.noWrap &&
    'white-space: nowrap; overflow: hidden;'};
  ${props =>
    props.fontFamily && `font-family: ${FontFamily[props.fontFamily]};`};
  ${props =>
    props.sizeMinMax &&
    `
    min-height: 0vw;
    font-size: clamp(
      ${getTextUnitSize(props.sizeMinMax[0])},
      ${clampBuilder(
        getTextPxSize(props.sizeMinMax[0]),
        getTextPxSize(props.sizeMinMax[1]),
        496,
        1200
      )},
      ${getTextUnitSize(props.sizeMinMax[1])}
    );

    line-height: clamp(
      ${lineHeightToPx(props.sizeMinMax[0])}px,
      ${clampBuilder(
        lineHeightToPx(props.sizeMinMax[0]),
        lineHeightToPx(props.sizeMinMax[1]),
        496,
        1200
      )},
      ${lineHeightToPx(props.sizeMinMax[1])}px
    );
  `}
`;

const Text = ({
  children,
  size = 'body-md',
  color,
  isInline,
  isDimmed,
  fontFamily = 'sans-serif',
  fontWeight = 'regular',
  hasEllipsis,
  ellipsisLines,
  noWrap,
  variant,
  htmlTag = 'span',
  alignment,
  sizeMinMax,
  fontSetting = 'normal',
  overflowWrap,
  ...props
}: TextProps &
  React.ComponentProps<typeof TextWrapper> &
  Omit<
    LensSpanProps,
    keyof TextProps & React.ComponentProps<typeof TextWrapper>
  >) => {
  if (isDimmed) {
    // eslint-disable-next-line no-console
    console.warn(textIsDimmedDeprecated);
  }

  // All heading sizes should use fontWeight bold
  if (size.includes('heading-')) {
    fontWeight = 'bold';
  }

  return (
    <TextWrapper
      size={variant ? variants[variant].size : size}
      color={color}
      isInline={isInline}
      isDimmed={isDimmed}
      fontWeight={variant ? variants[variant].fontWeight : fontWeight}
      fontFamily={fontFamily}
      hasEllipsis={hasEllipsis}
      ellipsisLines={ellipsisLines}
      noWrap={noWrap}
      variant={variant}
      as={htmlTag}
      alignment={alignment}
      sizeMinMax={sizeMinMax}
      fontSetting={fontSetting}
      overflowWrap={overflowWrap}
      {...props}
    >
      {children}
    </TextWrapper>
  );
};

export const availableSizes = [
  'body-sm',
  'body-md',
  'body-lg',
  'heading-sm',
  'heading-md',
  'heading-lg',
];
export const deprecatedSizes = [
  'small',
  'medium',
  'large',
  'xlarge',
  'xxlarge',
  'xxxlarge',
];
export const availableFontWeights = ['regular', 'medium', 'bold'];
export const availableHtmlTags = ['h1', 'h2', 'h3', 'h4', 'p', 'span', 'div'];
export const alignments = ['left', 'right', 'center'];

interface TextProps {
  size?: TextSize;
  fontWeight?: 'book' | 'regular' | 'medium' | 'bold';
  fontFamily?: 'sans-serif' | 'mono';
  variant?: 'body' | 'title' | 'mainTitle';
  alignment?: 'left' | 'right' | 'center';
  color?: string;
  isInline?: boolean;
  isDimmed?: boolean;
  hasEllipsis?: boolean;
  noWrap?: boolean;
  ellipsisLines?: number;
  htmlTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'div';
  children?: React.ReactNode;
  sizeMinMax?: string[];
  fontSetting?: 'tnum' | 'normal';
  overflowWrap?: 'normal' | 'break-word' | 'anywhere';
  /** @deprecated Usage of dclassName results in non-standard components */
  className?: string;
}

interface TextWrapperProps extends TextProps {
  as?: string;
}

export default Text;
